import React, { useState, useMemo, useEffect, useRef } from 'react';
import {
  Alert,
  Button,
  Checkbox,
  Divider,
  Input,
  Popover,
  PopoverAnchor,
  PopoverContent,
  Spinner,
} from '@billda/design-system';
import { SearchIcon } from '@billda/design-system';
import { useSitesSummary } from '../../sites/hooks/useSitesSummary';
import { What3WordsLink } from '../../../shared/components/atoms/What3WordsLink';

interface AddSitesPopoverProps {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  onAddSites: (siteIds: number[]) => void;
  userName: string;
  assignedSiteIds: number[];
  isSubmitting?: boolean;
}

export const AddSitesPopover: React.FC<AddSitesPopoverProps> = ({
  anchorEl,
  onClose,
  onAddSites,
  userName,
  assignedSiteIds,
  isSubmitting = false,
}) => {
  const [searchText, setSearchText] = useState('');
  const [selectedSiteIds, setSelectedSiteIds] = useState<Set<number>>(
    new Set()
  );

  const open = Boolean(anchorEl);
  const { data: sites = [], isLoading, error } = useSitesSummary();

  // Anchor the DS popover to the externally-owned anchor element.
  const anchorRef = useRef<HTMLElement | null>(null);
  anchorRef.current = anchorEl;

  // Reset state when popover closes
  useEffect(() => {
    if (!open) {
      setSearchText('');
      setSelectedSiteIds(new Set());
    }
  }, [open]);

  const unassignedSites = useMemo(
    () => sites.filter(site => !assignedSiteIds.includes(site.id)),
    [sites, assignedSiteIds]
  );

  const availableSites = useMemo(() => {
    return unassignedSites.filter(site =>
      site.name.toLowerCase().includes(searchText.toLowerCase())
    );
  }, [unassignedSites, searchText]);

  const allSitesAssigned = !isLoading && !error && unassignedSites.length === 0;

  const handleToggleSite = (siteId: number) => {
    setSelectedSiteIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(siteId)) {
        newSet.delete(siteId);
      } else {
        newSet.add(siteId);
      }
      return newSet;
    });
  };

  const handleAddSites = () => {
    if (selectedSiteIds.size > 0) {
      onAddSites(Array.from(selectedSiteIds));
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  return (
    <Popover
      open={open}
      onOpenChange={nextOpen => {
        if (!nextOpen) {
          handleClose();
        }
      }}
    >
      <PopoverAnchor virtualRef={anchorRef} />
      <PopoverContent
        align="start"
        sideOffset={4}
        data-testid="add-sites-popover"
        className="mt-1 max-h-[70vh] w-[400px] p-0"
      >
        <div className="p-4 pb-3">
          <h2 className="type-body-lg mb-2 font-medium">
            Add sites to {userName}
          </h2>

          {!allSitesAssigned && (
            <Input
              placeholder="Search sites..."
              value={searchText}
              onChange={e => setSearchText(e.target.value)}
              inputSize="sm"
              data-testid="search-sites-input"
              startAdornment={
                <SearchIcon
                  aria-hidden="true"
                  className="size-4 text-content-secondary"
                />
              }
            />
          )}
        </div>

        {allSitesAssigned ? (
          <div className="px-4 pb-4">
            <p className="type-body-md text-content-secondary">
              All available sites are already assigned.
            </p>
            <div className="mt-4 flex justify-end">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClose}
                data-testid="close-button"
              >
                Close
              </Button>
            </div>
          </div>
        ) : (
          <>
            <Divider />

            <div className="max-h-[calc(70vh-180px)] overflow-y-auto px-4 py-3">
              {isLoading && (
                <div className="flex justify-center p-6">
                  <Spinner size={32} />
                </div>
              )}

              {error && (
                <Alert severity="error" className="mb-2">
                  Failed to load sites. Please try again.
                </Alert>
              )}

              {!isLoading && !error && availableSites.length === 0 && (
                <div className="py-6 text-center">
                  <p className="type-body-md text-content-secondary">
                    No sites found matching your search.
                  </p>
                </div>
              )}

              {!isLoading && !error && availableSites.length > 0 && (
                <div className="flex flex-col">
                  {availableSites.map(site => (
                    <label
                      key={site.id}
                      className={`mx-0 flex cursor-pointer items-center gap-2 border-b border-border-default py-1.5 last:border-b-0 ${
                        isSubmitting ? 'cursor-not-allowed opacity-38' : ''
                      }`}
                    >
                      <Checkbox
                        checked={selectedSiteIds.has(site.id)}
                        onCheckedChange={() => handleToggleSite(site.id)}
                        disabled={isSubmitting}
                        data-testid={`site-checkbox-${site.id}`}
                        aria-label={site.name}
                      />
                      <span className="min-w-0">
                        <span className="type-body-md block">{site.name}</span>
                        {site.what3WordsAddress && (
                          <What3WordsLink
                            address={site.what3WordsAddress}
                            variant="caption"
                          />
                        )}
                      </span>
                    </label>
                  ))}
                </div>
              )}
            </div>

            <Divider />

            <div className="p-4 pt-3">
              <div className="flex items-center justify-between">
                <span className="type-body-md text-content-secondary">
                  {selectedSiteIds.size === 0
                    ? 'Select sites to add'
                    : selectedSiteIds.size === 1
                      ? '1 site selected'
                      : `${selectedSiteIds.size} sites selected`}
                </span>

                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={handleClose}
                    disabled={isSubmitting}
                    data-testid="cancel-button"
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="neutral"
                    size="sm"
                    onClick={handleAddSites}
                    disabled={selectedSiteIds.size === 0 || isSubmitting}
                    data-testid="add-sites-button"
                  >
                    {isSubmitting ? 'Adding...' : 'Add sites'}
                  </Button>
                </div>
              </div>
            </div>
          </>
        )}
      </PopoverContent>
    </Popover>
  );
};
